// Meta click-identity context forwarded on the payment path. The backend emits
// a server-side Conversions API `Purchase` on commitment-create and
// ticket-purchase; without these fields that event cannot be attributed to the
// ad click that caused it.
//
// The three keys here are optional `['sometimes','nullable']` body fields on
// both endpoints, deliberately declared with no `string` and no `max` rule so a
// malformed tracking value can never 422 a payment. That tolerance cuts both
// ways: Laravel's `sometimes` silently ignores unknown keys, and the API
// wrappers type `body` as `Record<string, unknown>`, so a key typo
// (`event_source_uri`) would raise no error anywhere in either stack and the
// field would simply never arrive. `MetaConversionContext` is the only thing
// that catches that class of mistake — keep every value flowing through it.
//
// DELIBERATE DIVERGENCE from `practices/nuxt/ssr-patterns.md` §"Absolute URL
// Construction Behind CloudFront", which requires
// `config.public.siteUrl || useRequestURL().origin`. Do not "fix" this to use
// `siteUrl`:
//   1. That rule exists because `useRequestURL().origin` resolves to the Lambda
//      function URL during SSR. That defect has no client-side analogue, and
//      this value is only ever captured inside a client-side submit handler —
//      where `useRequestURL()` is forbidden by the same doc and
//      `window.location` is definitionally correct.
//   2. The browser pixel reports the page it actually fired on. Reporting a
//      canonical host from the server would make the two halves of one dedup
//      pair disagree.
//   3. `events-beta.arvee.io` is a real serving host; `siteUrl` would name a
//      page the visitor was never on.
//
// `fbc` FALLBACK: when Meta's `_fbc` cookie is absent (pixel blocked, slow on
// the landing page, unconfigured, or the cookie was cleared), `fbc` is built
// from the `fbclid` captured on landing by `meta-click-id.client.ts` — see
// `app/utils/meta-click-id.ts`. The cookie always wins when present.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

import { readCookie } from '~/utils/cookies';
import { clearCapturedFbclid, readCapturedFbc } from '~/utils/meta-click-id';
import { isTrackingOptedOut } from '~/utils/tracking-consent';

export interface MetaConversionContext {
  fbc?: string;
  fbp?: string;
  event_source_url?: string;
}

// Defensive cap only. Meta owns the format of `_fbc` / `_fbp` and has changed
// it before, so a shape check of ours would start rejecting perfectly good
// cookies the day they extend it. A length cap cannot misfire that way.
const MAX_VALUE_LENGTH = 512;

// SECURITY — do not simplify away. Both consuming composables are also
// constructed on `app/pages/s/[shareToken].vue`, where the pathname is
// `/s/<shareToken>`. That token is an unguessable capability credential:
// `getEventByShareToken()` requires no auth, so anyone holding it can read the
// event. Emitting it would ship a working private-event link to Meta and into
// our own request logs. Nothing is lost by redacting it — `content_ids` and the
// event id already carry the event identity, and that page already
// self-canonicalizes to `/events/<id>` for exactly this reason.
const SHARE_PATH_PATTERN = /^\/s\/[^/]+/;

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  `_fbc` and `_fbp` ARE FORWARDED VERBATIM.                               │
 * │                                                                          │
 * │  Never hash them. Never `decodeURIComponent` them. Never validate them   │
 * │  against a format. Never reformat them.                                  │
 * │                                                                          │
 * │  Every *other* field in a Meta `user_data` object (email, first name,    │
 * │  last name, phone) is SHA-256 hashed, so a future reader will see these  │
 * │  two going out in the clear and be tempted to "fix the inconsistency".   │
 * │  Don't. Meta documents `fbc`/`fbp` as opaque identifiers it minted       │
 * │  itself and looks up by exact value.                                     │
 * │                                                                          │
 * │  Hashing them fails TOTALLY and SILENTLY: Meta accepts the payload,      │
 * │  returns success, matches nothing, and there is no error raised          │
 * │  anywhere. Attribution just quietly goes to zero.                        │
 * │                                                                          │
 * │  `arvee-backend`'s `app/Conversions/Meta/MetaClickIdentity.php` carries  │
 * │  the same warning on the receiving side.                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
function readClickIdentifier(name: string): string | undefined {
  const value = readCookie(name);
  if (value === null) return undefined;

  // An empty string is absence, not a value. `['sometimes','nullable']` with no
  // `string` rule happily accepts `''`, which reaches Meta as an empty `fbc`
  // and silently degrades match quality. Stated explicitly rather than left to
  // falsiness, which is easy for the next reader to miss.
  if (value === '') return undefined;

  if (value.length > MAX_VALUE_LENGTH) return undefined;

  return value;
}

/**
 * Meta click identity plus the page URL for the current client-side page view.
 *
 * `fbc` comes from the `_fbc` cookie when present, otherwise from the fbclid
 * captured on landing (`fb.1.<capturedAtMs>.<fbclid>`).
 *
 * Returns `{}` on the server, when `window` is unavailable, when the visitor
 * has opted out of tracking, and on any unexpected error.
 *
 * **Never throws.** This is called on the payment path, immediately before the
 * commit/purchase request is built. The call sites deliberately invoke it
 * *outside* their `try` blocks so a throw could not be mistaken for a failed
 * payment, but the guarantee lives here as well.
 */
export function buildMetaConversionContext(): MetaConversionContext {
  try {
    if (!import.meta.client) return {};

    // Guards on `window` rather than `document` because this reads
    // `window.location`.
    if (typeof window === 'undefined') return {};

    // Consent gate first, and it is load-bearing even though the pixel plugin
    // already skips installing itself on opt-out: `_fbp` is written at the
    // registrable-domain level, so one minted by another Arvee property's pixel
    // is readable here. Without this check `event_source_url` would also be
    // sent regardless of consent.
    //
    // Also discards any fbclid captured before a later opt-out (the
    // `do-not-sell` page, or GPC being turned on). Done here rather than in
    // `tracking-consent.ts` so consent handling never depends on Meta code.
    if (isTrackingOptedOut()) {
      clearCapturedFbclid();
      return {};
    }

    const context: MetaConversionContext = {};

    // The `_fbc` cookie wins; the captured-fbclid value is only a fallback.
    // Its timestamp is the CAPTURE time (Meta: "the timestamp when you first
    // observed or received this fbclid value"), not now.
    const fbc = readClickIdentifier('_fbc') ?? readCapturedFbc(Date.now());
    if (fbc !== undefined) context.fbc = fbc;

    const fbp = readClickIdentifier('_fbp');
    if (fbp !== undefined) context.fbp = fbp;

    // Origin + pathname only — no query string, no hash. Keeps `fbclid` and UTM
    // parameters out of the payload and out of our request logs, and keeps the
    // value comfortably inside the length cap (a long tagged URL truncated
    // mid-string would yield an invalid URL rather than a shorter valid one).
    const pathname = window.location.pathname.replace(
      SHARE_PATH_PATTERN,
      '/s/',
    );
    const sourceUrl = window.location.origin + pathname;
    if (sourceUrl.length <= MAX_VALUE_LENGTH) {
      context.event_source_url = sourceUrl;
    }

    return context;
  } catch {
    return {};
  }
}
